/**
 * The legacy GPS-tracking API's JSON, as apps/backend serves it (apps/backend/PORTING.md).
 *
 * Date-times are the FLEET's wall clock without an offset ("2026-10-20T18:59:14", the zone
 * the API runs in — Asia/Riyadh); dates are "2026-10-20". Use fleet-time.ts to turn them into
 * instants; never `new Date(value)` (that reads them in the browser's zone).
 */

export type FleetDateTime = string;
export type FleetDate = string;

export type Role = 'ADMIN' | 'OPERATOR' | 'VIEWER';
export type Status = 'ACTIVE' | 'INACTIVE';
export type VehicleType = 'CAR' | 'TRUCK' | 'BUS';
export type GeofenceType = 'RADIUS' | 'POLYGON';
export type EventType = 'ENTER' | 'EXIT';
export type AlertType = 'OVERSPEED' | 'GEOFENCE_ENTER' | 'GEOFENCE_EXIT' | 'IDLE_TIME' | 'HARSH_DRIVING';
export type AlertStatus = 'NEW' | 'ACKNOWLEDGED' | 'RESOLVED';
export type NotificationStatus = 'UNREAD' | 'READ';
export type TrackingStatus = 'MOVING' | 'STOPPED' | 'OFFLINE';
export type Language = 'en' | 'ar';

export const ROLES: readonly Role[] = ['ADMIN', 'OPERATOR', 'VIEWER'];
export const VEHICLE_TYPES: readonly VehicleType[] = ['CAR', 'TRUCK', 'BUS'];
export const ALERT_TYPES: readonly AlertType[] = ['OVERSPEED', 'GEOFENCE_ENTER', 'GEOFENCE_EXIT', 'IDLE_TIME', 'HARSH_DRIVING'];
export const ALERT_STATUSES: readonly AlertStatus[] = ['NEW', 'ACKNOWLEDGED', 'RESOLVED'];
export const TRACKING_STATUSES: readonly TrackingStatus[] = ['MOVING', 'STOPPED', 'OFFLINE'];

/** Spring Data's PageImpl. */
export interface Page<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  numberOfElements: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export interface PageQuery {
  page?: number;
  size?: number;
  /**
   * "property,asc|desc" — repeatable. Replaces the list's default order (on users, vehicles, alerts
   * and trips only since apps/backend's post-phase-1 changes; the Java API ignored it there).
   */
  sort?: string | string[];
}

/**
 * One failing field of a request (apps/backend after phase 1; the Java API sends `message` only).
 * `code` is the Bean Validation constraint: NotNull, NotBlank, Size, Email, Pattern, Min, Max,
 * DecimalMin, DecimalMax, Positive.
 */
export interface FieldError {
  /** "username"; "[2].thresholdValue" in a list body */
  field: string;
  code: string;
  /** the English text */
  message: string;
  /** {min, max} for Size, {value} for the bounds */
  params?: Record<string, number>;
}

/** dto/ErrorResponse */
export interface ErrorBody {
  timestamp?: string;
  status?: number;
  error?: string;
  message?: string;
  path?: string;
  /** validation failures, one per field (apps/backend after phase 1) */
  errors?: FieldError[];
}

export interface User {
  userId: number;
  username: string;
  fullName: string;
  email: string;
  phone: string | null;
  role: Role;
  status: Status;
  notes: string | null;
  profileImageUrl: string | null;
  language: string | null;
  lastLogin: FleetDateTime | null;
  createdAt: FleetDateTime;
  updatedAt: FleetDateTime;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
  user: User;
}

export interface Vehicle {
  vehicleId: number;
  vehicleName: string;
  plateNumber: string;
  vehicleType: VehicleType;
  model: string | null;
  year: number | null;
  color: string | null;
  status: Status;
  notes: string | null;
  linkedDeviceName: string | null;
  linkedDeviceImei: string | null;
  createdAt: FleetDateTime;
  updatedAt: FleetDateTime;
}

export interface VehicleInput {
  vehicleName: string;
  plateNumber: string;
  vehicleType: VehicleType;
  model?: string | null;
  year?: number | null;
  color?: string | null;
  notes?: string | null;
}

export interface Device {
  deviceId: number;
  deviceName: string;
  imei: string;
  simNumber: string;
  networkProvider: string | null;
  model: string | null;
  status: Status;
  notes: string | null;
  linkedVehicleName: string | null;
  linkedVehiclePlate: string | null;
  createdAt: FleetDateTime;
  updatedAt: FleetDateTime;
}

export interface DeviceInput {
  deviceName: string;
  imei: string;
  simNumber: string;
  networkProvider?: string | null;
  model?: string | null;
  notes?: string | null;
}

export interface Assignment {
  assignmentId: number;
  vehicleId: number;
  vehicleName: string;
  plateNumber: string;
  deviceId: number;
  deviceName: string;
  deviceImei: string;
  assignmentDate: FleetDate;
  status: Status;
  createdAt: FleetDateTime;
}

export interface PolygonPoint {
  latitude: number;
  longitude: number;
}

export interface Geofence {
  geofenceId: number;
  name: string;
  type: GeofenceType;
  centerLatitude: number | null;
  centerLongitude: number | null;
  radiusMeters: number | null;
  polygonPoints: PolygonPoint[];
  status: Status;
  notes: string | null;
  createdAt: FleetDateTime;
  updatedAt: FleetDateTime;
}

export interface GeofenceInput {
  name: string;
  type: GeofenceType;
  centerLatitude?: number | null;
  centerLongitude?: number | null;
  radiusMeters?: number | null;
  polygonPoints?: PolygonPoint[] | null;
  notes?: string | null;
}

export interface GeofenceEvent {
  eventId: number;
  vehicleId: number;
  vehicleName: string;
  geofenceId: number;
  geofenceName: string;
  eventType: EventType;
  eventTime: FleetDateTime;
}

/**
 * The values an alert's text is made of (apps/backend/src/engine/alert-text.ts), so a client can
 * word it in its own language. Speeds in km/h, acceleration in km/h per second, minutes.
 */
export interface AlertParamsByType {
  OVERSPEED: { vehicleName: string; speed: number; limit: number };
  /** `limit` is null when read back from an older alert's English message */
  HARSH_DRIVING: { vehicleName: string; acceleration: number; limit: number | null };
  /** `geofenceId` is null when read back from an older alert's English message */
  GEOFENCE_ENTER: { vehicleName: string; geofenceId: number | null; geofenceName: string };
  GEOFENCE_EXIT: { vehicleName: string; geofenceId: number | null; geofenceName: string };
  IDLE_TIME: { vehicleName: string; idleMinutes: number; limit: number };
}
export type AlertParams = AlertParamsByType[AlertType];

export interface Alert {
  alertId: number;
  vehicleId: number;
  vehicleName: string;
  alertType: AlertType;
  alertTime: FleetDateTime;
  latitude: number | null;
  longitude: number | null;
  speed: number | null;
  status: AlertStatus;
  createdAt: FleetDateTime;
  updatedAt: FleetDateTime;
  /** absent from the Java API; null on alerts stored before apps/backend kept them */
  params?: AlertParams | null;
}

/** The alert types the engine raises on many points in a row — they take a cooldown. */
export const COOLDOWN_ALERT_TYPES: readonly AlertType[] = ['OVERSPEED', 'HARSH_DRIVING'];

export interface AlertSetting {
  alertType: AlertType;
  enabled: boolean;
  /**
   * Whether the type's alerts are emailed, and to whom (at most 10 addresses; at least one while the
   * alert is raised and its email is on) — in place of the legacy WhatsApp number (2026-10-24).
   * Omitted from a PUT, they are left as they are.
   */
  emailEnabled: boolean;
  emailRecipients: string[];
  thresholdValue: number | null;
  /**
   * Minutes a vehicle stays quiet for this type after an alert (OVERSPEED, HARSH_DRIVING; 0 = every
   * point; null for the other types). Absent from the Java API — and omitted from a PUT, it is left
   * as it is.
   */
  cooldownMinutes?: number | null;
}

export interface Notification {
  notificationId: number;
  alertId: number;
  /** English, "OVERSPEED Alert" — word it from `alertType` instead where the API sends it */
  title: string;
  /** English — word it from `params` instead where the API sends them */
  message: string;
  createdTime: FleetDateTime;
  status: NotificationStatus;
  /** absent from the Java API */
  alertType?: AlertType;
  /** absent from the Java API; null when the message could not be read */
  params?: AlertParams | null;
}

export interface Trip {
  tripId: number;
  vehicleId: number;
  vehicleName: string;
  plateNumber: string;
  startTime: FleetDateTime;
  /** null while the trip is in progress (listed since apps/backend's post-phase-1 changes) */
  endTime: FleetDateTime | null;
  durationMinutes: number | null;
  distanceKm: number | null;
  avgSpeed: number | null;
  /** only on GET /api/trips/{id}, and only for a finished trip */
  maxSpeed: number | null;
  startLatitude: number;
  startLongitude: number;
  endLatitude: number | null;
  endLongitude: number | null;
}

export interface TripRoutePoint {
  latitude: number;
  longitude: number;
  speed: number | null;
  direction: number | null;
  timestamp: FleetDateTime;
}

export interface TripStop {
  stopId: number;
  latitude: number;
  longitude: number;
  startTime: FleetDateTime;
  endTime: FleetDateTime | null;
  durationMinutes: number | null;
}

export interface TrackingVehicle {
  vehicleId: number;
  vehicleName: string;
  plateNumber: string;
  deviceName: string;
  imei: string;
  latitude: number | null;
  longitude: number | null;
  speed: number | null;
  direction: number | null;
  status: TrackingStatus;
  lastUpdate: FleetDateTime | null;
}

export interface StatusCounts {
  movingCount: number;
  stoppedCount: number;
  offlineCount: number;
  totalCount: number;
}

export interface TrackingOverview extends StatusCounts {
  vehicles: TrackingVehicle[];
}

/** /topic/tracking — the vehicle's last position (deviceName/imei only while a device is assigned). */
export interface LivePosition {
  vehicleId: number;
  vehicleName: string;
  plateNumber: string;
  latitude: number;
  longitude: number;
  speed: number | null;
  direction: number | null;
  status: TrackingStatus;
  lastUpdate: FleetDateTime;
  deviceName?: string;
  imei?: string;
}

/** /topic/alerts */
export interface LiveAlert {
  alertId: number;
  vehicleName: string;
  alertType: AlertType;
  alertTime: FleetDateTime;
  message: string;
  /** absent from the Java API */
  params?: AlertParams | null;
}

// ── reports ──────────────────────────────────────────────────────────────────

export interface ReportWindow {
  vehicleId?: number | null;
  startDate: string;
  endDate: string;
}

export interface TripReportRow {
  vehicleId: number;
  vehicleName: string;
  /** "yyyy-MM-ddTHH:mm" */
  startTime: string;
  endTime: string | null;
  distanceKm: number | null;
  durationMinutes: number | null;
  avgSpeed: number;
  inProgress: boolean;
}

export interface VehicleActivityRow {
  vehicleName: string;
  totalDistanceKm: number;
  totalTrips: number;
  drivingTimeMinutes: number;
  idleTimeMinutes: number;
  stopTimeMinutes: number;
  maxSpeed: number;
}

export interface ActivityLocations {
  vehicleId: number;
  vehicleName: string;
  plateNumber: string;
  startDate: string;
  endDate: string;
  totalPoints: number;
  locations: Array<{ latitude: number; longitude: number; speed: number | null; direction: number | null; recordedAt: string }>;
}

export interface AlertReportRow {
  vehicleName: string;
  alertType: AlertType;
  alertTime: FleetDateTime;
  latitude: number | null;
  longitude: number | null;
  speed: number | null;
  status: AlertStatus;
}

export interface AlertReport {
  details: AlertReportRow[];
  summaryByType: Partial<Record<AlertType, number>>;
}

export interface GeofenceReportRow {
  vehicleName: string;
  geofenceName: string;
  enterTime: FleetDateTime;
  exitTime?: FleetDateTime;
  durationInsideMinutes?: number;
}

export type ReportType = 'trips' | 'vehicle-activity' | 'alerts' | 'geofences';
export type ExportFormat = 'csv' | 'excel' | 'pdf';
